import {useContext, useEffect, useMemo, useState, useCallback, useRef} from "react";
import {useSelector} from "react-redux"
import useDeepCompareEffect from "use-deep-compare-effect";
import moment from "moment/moment";
import isNumber from "lodash/isNumber";
import {AuthContext} from "context/AuthProvider";
import {ReservationContext} from "../../../context/ReservationProvider";
import {useTranslation} from "react-i18next";
import { selectVessel } from "store/slices/reservation/selectors";

const useReservationCalculator = ({
                                      contract_id,
                                      startDate,
                                      endDate,
                                      nature_of_travel,
                                      live_aboard,
                                      requestedRedeemNightsAmount,
                                      isTEMConnected,
                                      marinaId,
                                      addNew,
                                      values,
                                      emptyVesselSelected,
                                      vesselMissingDetails,
                                      onMaxNightsToRedeemLessThanRequested
                                  }) => {
    const { t } = useTranslation();

    const {loggedIn} = useContext(AuthContext);
    const {memoized_calculateReservationPrice} = useContext(ReservationContext);
    const selectedVessel = useSelector(selectVessel);
    const [calculationDone, setCalculationDone] = useState(true);
    const [calculatedPrice, setCalculatedPrice] = useState({});
    const [redeemNightsOptions, setRedeemNightsOptions] = useState([]);
    const calculateRef = useRef();

    useEffect(() => {
        // reset calculation
        if (calculationDone && !startDate && !endDate) {
            setCalculatedPrice({});
            setRedeemNightsOptions([]);
        }
    }, [calculationDone, startDate, endDate]);


        // price calculation and redeem nights setup

        const getReservationPrice = useCallback( async () => {
            if (startDate && endDate) {
                const start_date = moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
                const end_date = moment(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
                let data = {};
                if (!loggedIn || addNew || emptyVesselSelected) {
                    data = {
                        ...values,
                        hull_type: values.hull_type,
                        ...vesselMissingDetails,
                        updated_or_added_new_vessel: true
                    };
                } else {
                    data.vessel_id = selectedVessel?.id;
                }

                if (data.vessel_id || (data.length && data.beam && data.draft )) {
                    setCalculationDone(false);
                    const pricing = await memoized_calculateReservationPrice({
                        ...data,
                        marina_id: marinaId,
                        start_date,
                        end_date,
                        live_aboard,
                        nature_of_travel,
                        requested_redeem_nights_amount: requestedRedeemNightsAmount,
                        contract_id
                    }).catch(error => {
                        console.error("Couldn't calculate price for vessel", error);
                        setCalculationDone(true);
                    });
                    setCalculatedPrice({...pricing});
                    setCalculationDone(true);
                    if (isTEMConnected && pricing.max_nights_to_redeem) {
                        const opts = [
                            ...Array(pricing.max_nights_to_redeem).keys(),
                            pricing.max_nights_to_redeem
                        ]
                            .reverse()
                            .map(value => {
                                let label;
                                if (value === 0) {
                                    label = 'RESERVATION_WIDGET_NO_NIGHTS_COUNTER';
                                } else {
                                    if (value === pricing.max_nights_to_redeem) {
                                        label = 'RESERVATION_WIDGET_REDEEM_ALL';
                                    } else {
                                        label =
                                            value > 1
                                                ? 'RESERVATION_WIDGET_NIGHTS_COUNTER'
                                                : 'RESERVATION_WIDGET_NIGHT_COUNTER';
                                    }
                                }

                                return {
                                    value,
                                    label: t(label, {number_of_nights: value})
                                };
                            });
                        setRedeemNightsOptions(opts);
                        if (
                            !isNumber(requestedRedeemNightsAmount) ||
                            (isNumber(requestedRedeemNightsAmount) &&
                                requestedRedeemNightsAmount > pricing.max_nights_to_redeem)
                        ) {
                            onMaxNightsToRedeemLessThanRequested(pricing.max_nights_to_redeem);
                        }
                    }
                } else {
                    setCalculatedPrice({});
                }
            } else {
                setCalculatedPrice({});
            }
        },[ startDate,
            endDate,
            requestedRedeemNightsAmount,
            isTEMConnected,
            marinaId,
            addNew,
            values,
            emptyVesselSelected,
            vesselMissingDetails,
            onMaxNightsToRedeemLessThanRequested,
            loggedIn,
            selectedVessel,
            contract_id,
            live_aboard,
            nature_of_travel,
            memoized_calculateReservationPrice,
            t
        ]);

        calculateRef.current = getReservationPrice;

        // useEffect(() => {
        //     if(requestedRedeemNightsAmount > 0) {
        //         calculateRef.current()
        //     }
        // }, [requestedRedeemNightsAmount]);

    return useMemo(() => ({
        calculatedPrice,
        redeemNightsOptions,
        getReservationPrice

    }), [calculatedPrice, redeemNightsOptions, getReservationPrice]);
}

export default useReservationCalculator;
